import { Fragment, useMemo, useState } from 'react';
import {
  ChevronDownIcon, ChevronRightIcon, MagnifyingGlassIcon, XMarkIcon,
} from '@heroicons/react/24/outline';
import { useStockKpi, useStockKpiEngineer } from '../hooks/useDailyInstallations';
import {
  Note, PageHeader, Panel, States, num, selectClass, shortDate,
} from '../components/OpsPageKit';

// Stock Logging KPIs — how well each engineer records the stock they use.
//
// Built for ops to hold people to, so every rule leans towards fairness:
//   - only COMPLETED work counts; UTI, DNA and never-started jobs have nothing
//     to log and would mark engineers down for work that did not happen
//   - measured to the end of YESTERDAY, because the same-day rule gives an
//     engineer until midnight
//   - counted from the day job-card logging went live, not before
// Worst first throughout, and every percentage opens onto the actual work
// orders behind it, because "82%" is not something anyone can chase.
const PERIODS = [
  ['this-week', 'This week'],
  ['last-week', 'Last week'],
  ['this-month', 'This month'],
  ['since-live', 'Since go-live'],
];

const BAND = {
  good: { bg: '#DCFCE7', fg: '#166534', bar: '#16A34A' },
  warn: { bg: '#FEF3C7', fg: '#92400E', bar: '#D97706' },
  bad: { bg: '#FEE2E2', fg: '#991B1B', bar: '#DC2626' },
  none: { bg: '#F3F4F6', fg: '#6B7280', bar: '#9CA3AF' },
};

const rate = (v) => (v === null || v === undefined ? '—' : `${Math.round(v * 100)}%`);
const bandOf = (v, t) => {
  if (v === null || v === undefined) return 'none';
  if (v >= t.good) return 'good';
  if (v >= t.warn) return 'warn';
  return 'bad';
};
const fullDate = (d) => (d
  ? new Date(`${d}T00:00:00Z`).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
  : '');

function RatePill({ value, band }) {
  const b = BAND[band] || BAND.none;
  return (
    <span
      className="inline-block min-w-[3.25rem] rounded-full px-2 py-0.5 text-center text-xs font-bold tabular-nums"
      style={{ background: b.bg, color: b.fg }}
    >
      {rate(value)}
    </span>
  );
}

// A rate bar with the target marked on it, so "how far off" is visible without
// doing arithmetic against a number printed somewhere else.
function RateBar({ value, band, target }) {
  const b = BAND[band] || BAND.none;
  return (
    <div className="relative h-1.5 w-full rounded-full bg-gray-100">
      <div
        className="h-full rounded-full"
        style={{ width: `${Math.max(0, Math.min(value || 0, 1)) * 100}%`, background: b.bar }}
      />
      <div
        className="absolute -top-1 h-3.5 w-px bg-gray-400"
        style={{ left: `${target * 100}%` }}
        title={`Target ${Math.round(target * 100)}%`}
      />
    </div>
  );
}

function Kpi({
  label, value, sub, band,
}) {
  const b = BAND[band] || null;
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3">
      <p className="text-[11px] font-medium uppercase tracking-wide text-gray-500">{label}</p>
      <p className="mt-1 text-2xl font-bold tabular-nums" style={{ color: b ? b.fg : '#111827' }}>{value}</p>
      {sub ? <p className="text-xs text-gray-400">{sub}</p> : null}
    </div>
  );
}

function WeekTrend({ weeks, t }) {
  const max = Math.max(1, ...weeks.map((w) => w.completed));
  if (!weeks.length) {
    return <p className="px-4 py-8 text-center text-sm text-gray-400">No finished weeks yet.</p>;
  }
  return (
    <div className="px-4 py-3">
      <div className="flex items-end gap-3" style={{ minHeight: '9rem' }}>
        {weeks.map((w) => (
          <div key={w.week} className="flex flex-1 flex-col items-center">
            <div className="flex h-28 w-full items-end justify-center gap-1">
              <div
                className="w-1/3 max-w-[2.5rem] rounded-t bg-gray-200"
                style={{ height: `${(w.completed / max) * 100}%` }}
                title={`${w.completed} completed`}
              />
              <div
                className="w-1/3 max-w-[2.5rem] rounded-t"
                style={{
                  height: `${(w.logged / max) * 100}%`,
                  background: BAND[bandOf(w.rate, t)].bar,
                }}
                title={`${w.logged} logged`}
              />
            </div>
            <p className="mt-1.5 text-xs font-semibold tabular-nums" style={{ color: BAND[bandOf(w.rate, t)].fg }}>
              {rate(w.rate)}
            </p>
            <p className="text-[11px] text-gray-500">w/c {shortDate(w.week)}</p>
            {w.partial ? <p className="text-[10px] text-gray-400">so far</p> : null}
          </div>
        ))}
      </div>
      <div className="mt-2 flex items-center gap-4 text-[11px] text-gray-500">
        <span className="flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-sm bg-gray-200" />completed</span>
        <span className="flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-sm bg-emerald-500" />logged</span>
      </div>
    </div>
  );
}

// The work orders behind an engineer's percentage: unlogged first, then late,
// then empty cards, so the top of the list is always what needs chasing.
function EngineerDetail({ email, filters, t }) {
  const { data, isLoading, isError } = useStockKpiEngineer(email, filters);
  if (isLoading) return <p className="px-4 py-4 text-sm text-gray-400">Loading jobs…</p>;
  if (isError || !data) return <p className="px-4 py-4 text-sm text-red-500">Could not load this engineer's jobs.</p>;

  const needs = data.jobs.filter((j) => !j.logged || j.days_late > 0 || j.empty);
  const maxDay = Math.max(1, ...data.by_day.map((d) => d.completed));

  return (
    <div className="grid gap-4 bg-gray-50/70 px-4 py-4 lg:grid-cols-[16rem_1fr]">
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Day by day</p>
        <div className="mt-2 space-y-1.5">
          {data.by_day.map((d) => {
            const b = bandOf(d.rate, t);
            return (
              <div key={d.date} className="flex items-center gap-2 text-xs">
                <span className="w-16 shrink-0 text-gray-500">
                  {new Date(`${d.date}T00:00:00Z`).toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit' })}
                </span>
                <div className="relative h-3 flex-1 rounded-sm bg-gray-200" style={{ maxWidth: `${(d.completed / maxDay) * 100}%` }}>
                  <div
                    className="h-full rounded-sm"
                    style={{ width: `${d.completed ? (d.logged / d.completed) * 100 : 0}%`, background: BAND[b].bar }}
                  />
                </div>
                <span className="w-10 shrink-0 text-right tabular-nums text-gray-600">{d.logged}/{d.completed}</span>
              </div>
            );
          })}
        </div>
      </div>

      <div>
        <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
          {needs.length ? `${needs.length} jobs to look at` : 'Nothing to chase — every job logged on the day'}
        </p>
        {needs.length ? (
          <div className="mt-2 overflow-x-auto rounded-md border border-gray-200 bg-white">
            <table className="min-w-full divide-y divide-gray-100 text-xs">
              <thead className="bg-gray-50 text-left font-semibold uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-2.5 py-1.5">Date</th>
                  <th className="px-2.5 py-1.5">WO</th>
                  <th className="px-2.5 py-1.5">Address</th>
                  <th className="px-2.5 py-1.5">Job</th>
                  <th className="px-2.5 py-1.5">Issue</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {needs.map((j) => {
                  let issue;
                  if (!j.logged) issue = <span className="font-semibold text-red-700">Not logged</span>;
                  else if (j.days_late > 0) {
                    issue = (
                      <span className="font-semibold text-amber-700">
                        Logged {j.days_late} {j.days_late === 1 ? 'day' : 'days'} late
                      </span>
                    );
                  } else issue = <span className="font-semibold text-gray-600">Logged with no stock</span>;
                  return (
                    <tr key={j.id}>
                      <td className="whitespace-nowrap px-2.5 py-1.5 tabular-nums text-gray-500">{shortDate(j.date)}</td>
                      <td className="whitespace-nowrap px-2.5 py-1.5 font-medium text-gray-800">{j.wo || '—'}</td>
                      <td className="max-w-xs truncate px-2.5 py-1.5 text-gray-600" title={j.address}>{j.address}</td>
                      <td className="whitespace-nowrap px-2.5 py-1.5 text-gray-500">{j.install_status}</td>
                      <td className="whitespace-nowrap px-2.5 py-1.5">{issue}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>
    </div>
  );
}

const SORTS = {
  rate: (a, b) => (a.rate ?? 1) - (b.rate ?? 1) || b.not_logged - a.not_logged,
  not_logged: (a, b) => b.not_logged - a.not_logged,
  same_day: (a, b) => (a.same_day_rate ?? 1) - (b.same_day_rate ?? 1),
  completed: (a, b) => b.completed - a.completed,
  name: (a, b) => String(a.engineer).localeCompare(String(b.engineer)),
};

function StockLoggingKpis() {
  const [period, setPeriod] = useState('this-week');
  const [isp, setIsp] = useState('');
  const [manager, setManager] = useState('');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('rate');
  const [open, setOpen] = useState(null);

  const filters = useMemo(() => ({ period, isp, manager }), [period, isp, manager]);
  const {
    data, isLoading, isFetching, refetch, isError,
  } = useStockKpi(filters);

  const t = data?.thresholds || { good: 0.95, warn: 0.8 };
  const tot = data?.totals || {};
  const range = data?.range || {};

  const engineers = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = (data?.engineers || []).filter((e) => !q
      || `${e.engineer} ${e.email} ${e.manager || ''}`.toLowerCase().includes(q));
    return [...list].sort(SORTS[sort]);
  }, [data, search, sort]);

  const SortHead = ({ k, children, className = '' }) => (
    <th className={`px-3 py-2 ${className}`}>
      <button
        type="button"
        onClick={() => setSort(k)}
        className={`uppercase tracking-wide ${sort === k ? 'text-gray-900' : 'text-gray-500 hover:text-gray-800'}`}
      >
        {children}
        {sort === k ? ' ↓' : ''}
      </button>
    </th>
  );

  return (
    <div className="p-6">
      <PageHeader
        title="Stock Logging KPIs"
        subtitle="How consistently each engineer logs the stock they use on completed work."
        onRefresh={refetch}
        fetching={isFetching}
      >
        <select value={isp} onChange={(e) => setIsp(e.target.value)} className={selectClass}>
          <option value="">Both regions</option>
          <option value="Region N">Region N</option>
          <option value="Region B">Region B</option>
        </select>
        <select value={manager} onChange={(e) => { setManager(e.target.value); setOpen(null); }} className={selectClass}>
          <option value="">All managers</option>
          {(data?.options?.managers || []).map((m) => <option key={m} value={m}>{m}</option>)}
          <option value="(none)">No manager recorded</option>
        </select>
      </PageHeader>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <div className="inline-flex rounded-md border border-gray-300 bg-white p-0.5">
          {PERIODS.map(([k, l]) => (
            <button
              key={k}
              type="button"
              onClick={() => { setPeriod(k); setOpen(null); }}
              className={`rounded px-3 py-1 text-sm font-medium ${
                period === k ? 'bg-brand text-white' : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              {l}
            </button>
          ))}
        </div>
        {range.from ? (
          <p className="text-xs text-gray-500">
            {fullDate(range.from)} – {fullDate(range.to)} · completed jobs · to end of yesterday
          </p>
        ) : null}
      </div>

      <States isLoading={isLoading} isError={isError} what="stock logging KPIs" />

      {data && (
        <>
          {range.fell_back ? (
            <Note tone="brand">
              No day this week has finished yet, so this is <strong>last week</strong>. Today&apos;s jobs
              count from tomorrow — engineers have until midnight to log them.
            </Note>
          ) : null}
          {range.clamped ? (
            <Note>
              Counted from <strong>{fullDate(range.live_from)}</strong>, when job-card stock logging went
              live. Before that there was nothing to log against.
            </Note>
          ) : null}

          <div className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-5">
            <Kpi
              label="Logging rate"
              value={rate(tot.rate)}
              sub={`${num(tot.logged)} of ${num(tot.completed)} completed jobs`}
              band={tot.band}
            />
            <Kpi
              label="Logged same day"
              value={rate(tot.same_day_rate)}
              sub={`${num(tot.late)} logged late`}
              band={bandOf(tot.same_day_rate, t)}
            />
            <Kpi
              label="Not logged"
              value={num(tot.not_logged)}
              sub="completed jobs, nothing recorded"
              band={tot.not_logged ? 'bad' : 'good'}
            />
            <Kpi
              label={`Below ${Math.round(t.warn * 100)}%`}
              value={num(tot.below_warn)}
              sub={`of ${num(tot.engineers)} engineers`}
              band={tot.below_warn ? 'bad' : 'good'}
            />
            <Kpi
              label="Logged everything"
              value={num(tot.perfect)}
              sub={`${num(tot.empty_cards)} empty install cards`}
            />
          </div>

          <div className="mt-4 grid gap-4 lg:grid-cols-2">
            <Panel title="Week by week">
              <WeekTrend weeks={data.trend} t={t} />
            </Panel>

            <Panel
              title="By manager"
              right={manager
                ? <button type="button" onClick={() => setManager('')} className="text-xs text-gray-500 hover:text-gray-800">Show all</button>
                : <span className="text-xs text-gray-400">click a team to filter</span>}
            >
              <div className="max-h-[17rem] overflow-auto">
                <table className="min-w-full text-sm">
                  <tbody className="divide-y divide-gray-100">
                    {data.managers.map((m) => (
                      <tr
                        key={m.manager}
                        onClick={() => { setManager(m.manager); setOpen(null); }}
                        className="cursor-pointer hover:bg-gray-50"
                      >
                        <td className="px-3 py-1.5">
                          <p className="font-medium text-gray-800">{m.manager}</p>
                          <p className="text-[11px] text-gray-400">
                            {m.engineers} {m.engineers === 1 ? 'engineer' : 'engineers'}
                            {m.below_warn ? ` · ${m.below_warn} below ${Math.round(t.warn * 100)}%` : ''}
                          </p>
                        </td>
                        <td className="w-28 px-3 py-1.5"><RateBar value={m.rate} band={m.band} target={t.good} /></td>
                        <td className="px-3 py-1.5 text-right"><RatePill value={m.rate} band={m.band} /></td>
                        <td className="whitespace-nowrap px-3 py-1.5 text-right text-xs tabular-nums text-gray-500">
                          {m.not_logged ? `${num(m.not_logged)} unlogged` : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Panel>
          </div>

          <div className="mt-4">
            <Panel
              title={`Engineers${manager ? ` — ${manager}'s team` : ''}`}
              right={(
                <div className="relative">
                  <MagnifyingGlassIcon className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Find engineer…"
                    className="w-44 rounded-md border border-gray-300 py-1 pl-7 pr-6 text-xs focus:border-brand focus:outline-none"
                  />
                  {search ? (
                    <button type="button" onClick={() => setSearch('')} className="absolute right-1 top-1/2 -translate-y-1/2 text-gray-400">
                      <XMarkIcon className="h-3.5 w-3.5" />
                    </button>
                  ) : null}
                </div>
              )}
            >
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-left text-xs font-semibold">
                    <tr>
                      <th className="w-8 px-2 py-2" />
                      <SortHead k="name">Engineer</SortHead>
                      <SortHead k="completed" className="text-right">Completed</SortHead>
                      <SortHead k="not_logged" className="text-right">Not logged</SortHead>
                      <SortHead k="rate">Logging rate</SortHead>
                      <SortHead k="same_day" className="text-right">Same day</SortHead>
                      <th className="px-3 py-2 text-right uppercase tracking-wide text-gray-500">Late</th>
                      <th className="px-3 py-2 text-right uppercase tracking-wide text-gray-500">Empty</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {engineers.map((e) => {
                      const isOpen = open === e.email;
                      return (
                        <Fragment key={e.email}>
                          <tr
                            onClick={() => setOpen(isOpen ? null : e.email)}
                            className={`cursor-pointer ${isOpen ? 'bg-brand-light/50' : 'hover:bg-gray-50'}`}
                          >
                            <td className="px-2 py-2 text-gray-400">
                              {isOpen ? <ChevronDownIcon className="h-4 w-4" /> : <ChevronRightIcon className="h-4 w-4" />}
                            </td>
                            <td className="px-3 py-2">
                              <p className="font-medium text-gray-800">{e.engineer}</p>
                              <p className="text-[11px] text-gray-400">{e.manager || 'no manager recorded'}</p>
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums text-gray-700">{num(e.completed)}</td>
                            <td className={`px-3 py-2 text-right font-semibold tabular-nums ${e.not_logged ? 'text-red-700' : 'text-gray-300'}`}>
                              {e.not_logged ? num(e.not_logged) : '0'}
                            </td>
                            <td className="px-3 py-2">
                              <div className="flex min-w-[10rem] items-center gap-2">
                                <RatePill value={e.rate} band={e.band} />
                                <div className="flex-1"><RateBar value={e.rate} band={e.band} target={t.good} /></div>
                              </div>
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums text-gray-600">{rate(e.same_day_rate)}</td>
                            <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums text-gray-500">
                              {e.late ? `${e.late} · ${e.avg_days_late.toFixed(1)}d` : '—'}
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums text-gray-500">{e.empty_cards || '—'}</td>
                          </tr>
                          {isOpen ? (
                            <tr>
                              <td colSpan={8} className="p-0">
                                <EngineerDetail email={e.email} filters={filters} t={t} />
                              </td>
                            </tr>
                          ) : null}
                        </Fragment>
                      );
                    })}
                    {!engineers.length ? (
                      <tr>
                        <td colSpan={8} className="px-3 py-10 text-center text-gray-400">
                          {search ? `No engineer matching “${search}”.` : 'No completed jobs in this period.'}
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            </Panel>
          </div>

          <Note tone="brand">
            <p>
              <strong>How this is measured.</strong> Only completed work counts — installs, service calls,
              remediations and externals marked complete. UTI, DNA and jobs that never started are left
              out, because there is usually nothing to log. A job is <em>logged</em> once its stock card is
              submitted, and <em>same day</em> if that happened on the day of the job. <em>Empty</em> counts
              install cards submitted with no stock at all.
            </p>
            <p className="mt-1">
              Bands: green {Math.round(t.good * 100)}% and above, amber {Math.round(t.warn * 100)}–{Math.round(t.good * 100)}%,
              red below {Math.round(t.warn * 100)}%. These are working thresholds, not an agreed target — the
              marker on each bar sits at {Math.round(t.good * 100)}%.
            </p>
          </Note>
        </>
      )}
    </div>
  );
}

export default StockLoggingKpis;
